import { FormikValues } from "formik";
import {
  DocumentType,
  Field,
  Option,
  SecuritiesOptions,
  SecuritiesFormikValues,
  Step,
  StepEndpointSecurities,
  StepTwoSecurities,
} from "../../types";
import styles from "../../styles/securities.module.scss";
import { validationsForLanguage } from "../../validations";
import {
  maxFileSize,
  minFileSize,
  parseDateToSend,
  serializableFileAsFile,
  validDocumentImageTypes,
} from "../../utils";
import {
  addSendedStep,
  onUpdateCompletedStep,
  onUpdateStep,
} from "../../reducers/securitiesReducer";
import {
  fileInfoText,
  labels,
  placeholders,
  texts,
  title,
} from "../../i18n/securities/stepTwo";
import {
  documentTypesOptions,
  yesOrNoBoolOptions,
} from "../../i18n/securities/options";
import { RootState } from "../../app/store";
import { holder } from "../../i18n/securities/preconfirmationStep";
import {
  CreateDraftProps,
  sendIndividualDocument,
  sendStep,
} from "../../connectors/connectors";
import { heicToJpeg } from "../../app/utils";

// Initial identification form
const StepTwo = ({
  language,
  countriesOptions,
  citizenshipOptions,
  currentHolder,
  saveFileToFirestore,
}: {
  language: "es" | "en";
  countriesOptions: Option[];
  citizenshipOptions: Option[];
  currentHolder: number;
  saveFileToFirestore?: (fileName: string, type: DocumentType) => void;
}): Step => {
  const { formSectionTitle, documentImagesLabel } = styles;

  const validations = validationsForLanguage(language);

  const documentFrontTypes: Record<
    SecuritiesOptions["documentType"],
    DocumentType
  > = {
    DNI: "DNI_FRONT",
    PASSPORT: "PASSPORT_FIRST_PAGE",
  };
  const documentBackTypes: Record<
    SecuritiesOptions["documentType"],
    DocumentType
  > = {
    DNI: "DNI_BACK",
    PASSPORT: "PASSPORT_SECOND_PAGE",
  };

  // Generate as many `documentFront` fields as there are document types, each
  // condintionally showing when that document type is chosen. This allows this
  // field to be customized for when a certain type of document was chosen
  const documentImageFront = (): Field[] =>
    documentTypesOptions[language].map(({ value: documentType }) => ({
      name: `documentFront${documentType}`,
      type: "file",
      label:
        documentType === "PASSPORT"
          ? labels[language].passportFront
          : labels[language].documentFront,
      validation: validations.fileRequired(
        validDocumentImageTypes,
        maxFileSize,
        minFileSize
      ),
      initialValue: "",
      infoText: fileInfoText(maxFileSize)[language],
      infoTextPosition: "bottom",
      maxSize: maxFileSize,
      validTypes: validDocumentImageTypes,
      conditionalBool(values, _, state) {
        if (!state) return false;
        const usCitizen = state.securities.USCitizen;
        return (
          (usCitizen && documentType === "PASSPORT") ||
          (!usCitizen && values.documentTypeAR === documentType)
        );
      },
    }));

  // Same as above but for `documentBack` field
  const documentImageBack = (): Field[] =>
    documentTypesOptions[language].map(({ value: documentType }) => ({
      name: `documentBack${documentType}`,
      type: "file",
      label:
        documentType === "PASSPORT"
          ? labels[language].passportBack
          : labels[language].documentBack,
      validation:
        documentType === "PASSPORT"
          ? validations.fileNotRequired(
              validDocumentImageTypes,
              maxFileSize,
              minFileSize
            )
          : validations.fileRequired(
              validDocumentImageTypes,
              maxFileSize,
              minFileSize
            ),
      initialValue: "",
      infoText: fileInfoText(maxFileSize)[language],
      infoTextPosition: "bottom",
      maxSize: maxFileSize,
      validTypes: validDocumentImageTypes,
      conditionalBool(values, _, state) {
        if (!state) return false;
        const usCitizen = state.securities.USCitizen;
        return (
          (usCitizen && documentType === "PASSPORT") ||
          (!usCitizen && values.documentTypeAR === documentType)
        );
      },
    }));

  return {
    step: 2,
    name: `${title[language]} ${
      currentHolder !== 1 ? `- (${holder[language]} ${currentHolder})` : ""
    }`,
    titleOfStep: title[language],
    label: title[language],
    fields: [
      {
        label: "",
        initialValue: "",
        type: "label",
        name: "section1",
        text: (
          <>
            <p className={formSectionTitle}>{texts[language].section1}</p>
          </>
        ),
      },
      {
        name: "USResident",
        label: labels[language].USResident,
        type: "radio",
        placeholder: placeholders[language].USResident,
        validation: validations.radioOrSingleSelectRequired(
          yesOrNoBoolOptions[language]
        ),
        initialValue: "",
        options: yesOrNoBoolOptions[language],
        formatInPreConfirmation: (value: string) =>
          value === "SI" ? "Sí" : "No",
      },
      {
        name: "residenceCountry",
        type: "select",
        label: labels[language].residenceCountry,
        placeholder: placeholders[language].residenceCountry,
        validation: validations.radioOrSingleSelectRequired(countriesOptions),
        options: countriesOptions,
        selectType: "single",
        initialValue: "",
      },
      {
        name: "firstCitizenship",
        type: "select",
        label: labels[language].firstCitizenship,
        placeholder: placeholders[language].firstCitizenship,
        validation: validations.radioOrSingleSelectRequired(citizenshipOptions),
        options: citizenshipOptions,
        selectType: "single",
        initialValue: "",
      },
      {
        name: "additionalCitizenshipBool",
        label: labels[language].additionalCitizenshipBool,
        type: "radio",
        placeholder: "",
        validation: validations.radioOrSingleSelectRequired(
          yesOrNoBoolOptions[language]
        ),
        initialValue: "",
        options: yesOrNoBoolOptions[language],
        formatInPreConfirmation: (value: string) =>
          value === "SI" ? "Sí" : "No",
      },
      {
        name: "additionalCitizenship",
        type: "select",
        label: labels[language].additionalCitizenship,
        placeholder: placeholders[language].additionalCitizenship,
        validation: validations.radioOrSingleSelectRequired(citizenshipOptions),
        options: citizenshipOptions,
        selectType: "single",
        initialValue: "",
        dontScrollTo: true,
        conditional: {
          name: "additionalCitizenshipBool",
          value: "SI",
        },
      },
      {
        label: "",
        initialValue: "",
        type: "label",
        name: "section2",
        text: (
          <>
            <p className={formSectionTitle}>{texts[language].section2}</p>
          </>
        ),
      },
      {
        name: "socialSecurityNumber",
        type: "text",
        disabled: true,
        label: labels[language].socialSecurityNumber,
        validation: validations.ssnRequired,
        initialValue: "",
        placeholder: placeholders[language].socialSecurityNumber,
        conditionalBool: (_1, _2, state?: RootState) =>
          !!state?.securities.USCitizen,
      },
      {
        name: "documentTypeAR",
        type: "select",
        label: labels[language].documentType,
        placeholder: placeholders[language].documentType,
        validation: validations.radioOrSingleSelectRequired(
          documentTypesOptions[language]
        ),
        options: documentTypesOptions[language],
        selectType: "single",
        conditionalBool: (_1, _2, state?: RootState) =>
          !state?.securities.USCitizen,
        initialValue: "",
      },
      {
        name: "documentTypeUS",
        type: "select",
        label: labels[language].documentType,
        placeholder: placeholders[language].documentType,
        validation: validations.radioOrSingleSelectRequired(
          documentTypesOptions[language]
        ),
        options: documentTypesOptions[language],
        selectType: "single",
        conditionalBool: (_1, _2, state?: RootState) =>
          !!state?.securities.USCitizen,
        initialValue: "PASSPORT",
        disabled: true,
      },
      {
        name: "documentNumber",
        type: "text",
        label: labels[language].documentNumber,
        validation: validations.smallStringRequired,
        initialValue: "",
        placeholder: placeholders[language].documentNumber,
        conditionalBool: (_1, _2, state?: RootState) =>
          !state?.securities.USCitizen,
      },
      {
        name: "passportNumber",
        type: "text",
        label: labels[language].passportNumber,
        validation: validations.smallStringRequired,
        initialValue: "",
        placeholder: placeholders[language].passportNumber,
        conditionalBool: (_1, _2, state?: RootState) =>
          !!state?.securities.USCitizen,
      },
      {
        name: "documentCountry",
        type: "select",
        label: labels[language].documentCountry,
        placeholder: placeholders[language].documentCountry,
        validation: validations.radioOrSingleSelectRequired(countriesOptions),
        options: countriesOptions,
        selectType: "single",
        initialValue: "",
      },
      {
        name: "documentEmisionDate",
        label: labels[language].documentEmisionDate,
        type: "date",
        initialValue: "",
        placeholder: placeholders[language].documentEmisionDate,
        validation: validations.previousDateRequired,
        columns: 2,
      },
      {
        name: "documentExpirationDate",
        label: labels[language].documentExpirationDate,
        type: "date",
        initialValue: "",
        placeholder: placeholders[language].documentExpirationDate,
        validation: validations.futureDateRequired,
        columns: 2,
      },
      {
        label: "",
        initialValue: "",
        type: "label",
        name: "documentImagesLabelAR",
        conditionalBool: (_1, _2, state?: RootState) =>
          !state?.securities.USCitizen,
        text: (
          <div className={documentImagesLabel}>
            <h2>{texts[language].documentImagesLabelTitle.AR}</h2>
            <p>{texts[language].documentImagesLabelBody.AR}</p>
          </div>
        ),
      },
      {
        label: "",
        initialValue: "",
        type: "label",
        name: "documentImagesLabelUS",
        conditionalBool: (_1, _2, state?: RootState) =>
          !!state?.securities.USCitizen,
        text: (
          <div className={documentImagesLabel}>
            <h2>{texts[language].documentImagesLabelTitle.US}</h2>
            <p>{texts[language].documentImagesLabelBody.US}</p>
          </div>
        ),
      },
      ...documentImageFront(),
      ...documentImageBack(),
      {
        name: "taxId",
        type: "text",
        label: labels[language].taxId,
        validation: validations.smallStringRequired,
        initialValue: "",
        placeholder: placeholders[language].taxId,
        preconfirmationLabel: labels[language].preconfirmationTaxId,
        disabled: true,
        conditionalBool: (_1, _2, state?: RootState) =>
          !state?.securities.USCitizen,
      },
    ],
    onContinue: async (values: FormikValues, dispatch, state) => {
      try {
        if (!state || !state.validations || !dispatch)
          throw new Error("Error: state or dispatch not found");

        const formValues = values as SecuritiesFormikValues["stepTwo"];

        const {
          USResident,
          residenceCountry,
          firstCitizenship,
          additionalCitizenshipBool,
          additionalCitizenship,
          documentTypeAR = "DNI",
          documentTypeUS = "PASSPORT",
          documentNumber = "",
          passportNumber = "",
          documentCountry,
          documentEmisionDate,
          documentExpirationDate,
        } = formValues;

        const {
          USCitizen,
          initialValidationData,
          currentHolder,
          sendedSteps,
          taxId,
        } = state.securities;

        // Upload corresponding documents
        const documentType = USCitizen ? documentTypeUS : documentTypeAR;

        const documentFront = formValues[`documentFront${documentType}`];
        const documentBack = formValues[`documentBack${documentType}`];

        if (documentFront) {
          const file = await heicToJpeg(serializableFileAsFile(documentFront));
          await sendIndividualDocument({
            draftUUID: state.securities.draftUUID,
            cuit: taxId,
            documentType: documentFrontTypes[documentType],
            file,
          });
          saveFileToFirestore?.(file.name, documentFrontTypes[documentType]);
        }

        if (documentBack) {
          const file = await heicToJpeg(serializableFileAsFile(documentBack));
          await sendIndividualDocument({
            draftUUID: state.securities.draftUUID,
            cuit: taxId,
            documentType: documentBackTypes[documentType],
            file,
          });
          saveFileToFirestore?.(file.name, documentBackTypes[documentType]);
        }

        if (currentHolder === 1) {
          const step: StepTwoSecurities = {
            "@type": "2_SECURITY",
            residence: {
              isAmericanResident: USResident === "SI",
              fiscalResidenceCountry: residenceCountry,
              primaryCitizenship: firstCitizenship,
              hasAdditionalCitizenship: additionalCitizenshipBool === "SI",
              additionalCitizenship:
                additionalCitizenshipBool === "SI"
                  ? additionalCitizenship
                  : undefined,
            },
            identityInformation: {
              identificationType: USCitizen ? documentTypeUS : documentTypeAR,
              number: USCitizen
                ? passportNumber
                : documentNumber?.toString() || "",
              documentIssuingCountry: documentCountry,
              documentIssuingDate: parseDateToSend(documentEmisionDate),
              documentExpirationDate: parseDateToSend(documentExpirationDate),
            },
          };

          // Previous sent steps
          const previousSteps = sendedSteps.filter(
            (step: StepEndpointSecurities) => step["@type"] !== "2_SECURITY"
          );

          const body: CreateDraftProps["body"] = {
            companyType: "ALLARIA_SECURITY",
            personType: "PHYSICAL",
            email: initialValidationData.email,
            steps: [...previousSteps, step],
          };

          await sendStep({ stepId: 2, body });
          dispatch(addSendedStep(step));
        }
        dispatch(onUpdateStep(3));
        dispatch(onUpdateCompletedStep(2));
      } catch (error) {
        console.error("Error sending step two:", error);
        throw error;
      }
    },
  };
};

export default StepTwo;
